import React, { useLayoutEffect, useState } from "react";
import { ReactComponent as InfoIcon } from "icons/info-grey.svg";
import { useDispatch } from "react-redux";
import { hideModal } from "slices/app/actions/modals";
import { modalTypes } from "constants/types";
import Modal from "@visiblethread/react-components/dist/components/Modal";
import Button, {
  buttonVariants,
} from "@visiblethread/react-components/dist/components/Button";
import PropTypes from "prop-types";
import { exportXlsx } from "slices/dictionarySearch/actions/dictionarySearch";
import { exportOrigin } from "layout/dictionarySearch/constants";
import TwoOptionsRow from "components/TwoOptionsRow";
import OnOffRow from "components/OnOffRow";
import IconWithTooltip from "components/IconWithTooltip";
import { getFromLS } from "@visiblethread/react-components/dist/utils";
import { DS_EXPORT_CONTENT_CLSX_LS_KEY } from "constants/dictionarySearch";

const getHeaderText = (origin, docsWithTerms) => {
  switch (origin) {
    case exportOrigin.catShred:
      return "Category Shred";
    case exportOrigin.simpleShred:
      return "Simple Shred";
    case exportOrigin.documents:
      return `Export${docsWithTerms.length > 1 ? " Multiple" : ""} Document${
        docsWithTerms.length > 1 ? "s" : ""
      }`;
    default:
      return "";
  }
};

const ExportContentModalXlsx = ({ documentsWithTerms, origin, extensions }) => {
  const haveCheckedItems =
    documentsWithTerms.filter((entry) => entry.selectedTerms.length > 0)
      .length > 0;
  const dispatch = useDispatch();
  const [exportSelectedData, setExportSelectedData] = useState(false);
  const [splitBySentence, setSplitBySentence] = useState(false);
  const [newRowOnHit, setNewRowOnHit] = useState(false);
  const [newTabPerDocument, setNewTabPerDocument] = useState(true);
  const [highlightSelectedTerms, setHighlightSelectedTerms] =
    useState(haveCheckedItems);
  const [showGridlines, setShowGridlines] = useState(true);
  const [repeatRefs, setRepeatRefs] = useState(false);
  const [displayRefsInContent, setDisplayRefsInContent] = useState(false);
  const [wrapText, setWrapText] = useState(true);
  const [pagesToIgnore, setPagesToIgnore] = useState("");
  const [textToIgnore, setTextToIgnore] = useState("");
  const [includeMacros, setIncludeMacros] = useState(false);
  const shredOptionsRequest = {
    exportSelectedData,
    splitBySentence,
    newRowOnHit,
    newTabPerDocument,
    highlightAllTerms: !highlightSelectedTerms,
    showGridlines,
    repeatRefs,
    displayRefsInContent,
    wrapText,
    pagesToIgnore,
    textToIgnore,
    macroEnabled: includeMacros,
  };

  useLayoutEffect(() => {
    try {
      const configStringified = getFromLS(DS_EXPORT_CONTENT_CLSX_LS_KEY);

      if (!configStringified) {
        throw new Error("Export content xlsx config: empty");
      }

      const config = JSON.parse(configStringified);

      setExportSelectedData(config.exportSelectedData);
      setSplitBySentence(config.splitBySentence);
      setNewRowOnHit(config.newRowOnHit);
      setNewTabPerDocument(config.newTabPerDocument);
      setHighlightSelectedTerms(!config.highlightAllTerms);
      setRepeatRefs(config.repeatRefs);
      setDisplayRefsInContent(config.displayRefsInContent);
      setShowGridlines(config.showGridlines);
      setWrapText(config.wrapText);
      setPagesToIgnore(config.pagesToIgnore);
      setTextToIgnore(config.textToIgnore);
      setIncludeMacros(config.macroEnabled);
    } catch (e) {}
  }, []);

  const handleCancel = () => {
    dispatch(hideModal(modalTypes.EXPORT_CONTENT));
  };

  const dataOptions = () => (
    <div className="export_content_xlsx__body__data_opts">
      <span>Data Options</span>
      <TwoOptionsRow
        option1="Export all content"
        option2="Export displayed content"
        state={exportSelectedData}
        tooltipText="Export all content will output every paragraph to Excel.<br><br>Export displayed content just outputs what's in the content pane only."
        setState={setExportSelectedData}
      />
      <TwoOptionsRow
        option1="Split by paragraph"
        option2="Split by sentence"
        state={splitBySentence}
        tooltipText="Split by paragraph creates one row per paragraph, whereas split by sentence creates one row per sentence."
        setState={setSplitBySentence}
      />
      <OnOffRow
        text="New row on dictionary hit"
        state={newRowOnHit}
        tooltipText="Turning this on splits a sentence, and forces a new row once we find the dictionary term."
        setState={setNewRowOnHit}
      />
      {documentsWithTerms.length > 1 && (
        <OnOffRow
          text="New tab for every document"
          state={newTabPerDocument}
          tooltipText="Turning this on outputs a tab per document, whereas if you set it to off, we'll output everything in one tab."
          setState={setNewTabPerDocument}
        />
      )}
      <OnOffRow
        text="Repeat Refs"
        state={repeatRefs}
        tooltipText="Turning this on causes the ref to repeat. This continues until we see a new ref value in the Ref column."
        setState={setRepeatRefs}
      />
      <OnOffRow
        text="Display Refs in content column"
        state={displayRefsInContent}
        tooltipText="Turning this on outputs refs both in the Ref and Content columns. Leaving it off outputs the ref only in the Ref column."
        setState={setDisplayRefsInContent}
      />
    </div>
  );

  const presentationOptions = () => (
    <div className="export_content_xlsx__body__presentation_opts">
      <span>Presentation Options</span>
      <TwoOptionsRow
        option1="Highlight all terms"
        option2="Highlight selected terms"
        state={highlightSelectedTerms}
        tooltipText="Highlight all terms highlights every dictionary term regardless of whether it's checked in VT Docs.<br><br>Highlight selected terms only highlights those terms you have checked."
        setState={setHighlightSelectedTerms}
      />
      <OnOffRow
        text="Show gridlines"
        state={showGridlines}
        tooltipText="Turn on/off gridlines in Excel."
        setState={setShowGridlines}
      />
    </div>
  );

  const wordSpecific = () => (
    <div
      className={`export_content_xlsx__body__word_specific${
        !extensions.wordSelected ? " disabled" : ""
      }`}
    >
      <span>MS Word Specific Options</span>
      <OnOffRow
        text="Wrap text in tables"
        state={wrapText}
        tooltipText="Turning this on wraps text in generated MS Word table cells."
        disabled={!extensions.wordSelected}
        setState={setWrapText}
      />
    </div>
  );

  const pdfSpecific = () => {
    const onChange = (e) => {
      const inputValue = e.target.value;
      // Remove characters other than numbers, commas, and dashes
      const sanitizedValue = inputValue.replace(/[^0-9,-]/g, "");
      setPagesToIgnore(sanitizedValue);
    };

    return (
      <div
        className={`export_content_xlsx__body__pdf_specific${
          !extensions.pdfSelected ? " disabled" : ""
        }`}
      >
        <span>PDF Specific Options</span>
        <span className="description">
          Ignore Pages
          <IconWithTooltip
            icon={<InfoIcon width={11} height={11} />}
            tooltipText="Use this to ignore pages or page ranges in PDFs."
          />
        </span>
        <input
          placeholder="E.g. 1,2,3-6,78,79"
          value={pagesToIgnore}
          onChange={(e) => onChange(e)}
          disabled={!extensions.pdfSelected}
        />
      </div>
    );
  };

  const advancedOptions = () => {
    return (
      <div className="export_content_xlsx__body__advanced_opts">
        <span>Advanced Options</span>
        <span className="description">
          Ignore Text
          <IconWithTooltip
            icon={<InfoIcon width={11} height={11} />}
            tooltipText="Use this to ignore certain text in your output. If the sentence contains the specified text, then the full sentence will be ignored."
          />
        </span>
        <input
          placeholder="E.g. APPENDIX, COPYRIGHT"
          value={textToIgnore}
          onChange={(e) => setTextToIgnore(e.target.value)}
        />
        <OnOffRow
          text="Include macros to customize output"
          state={includeMacros}
          tooltipText="VT Docs allows you embed macros into generated Excel outputs. Turn this on to include macros."
          setState={setIncludeMacros}
        />
      </div>
    );
  };

  const modalBody = () => (
    <div className="export_content_xlsx__body">
      {dataOptions()}
      {presentationOptions()}
      {wordSpecific()}
      {pdfSpecific()}
      {advancedOptions()}
    </div>
  );

  const handleExportClick = () => {
    dispatch(exportXlsx(documentsWithTerms, shredOptionsRequest, origin));
  };

  return (
    <Modal
      data-testid="export-content-modal"
      className="fade-in export_content_xlsx"
      show
      onDismiss={handleCancel}
      backdrop="static"
      centered
    >
      <Modal.Header closeButton>
        <b>{getHeaderText(origin, documentsWithTerms)}</b>
      </Modal.Header>
      <Modal.Body>{modalBody()}</Modal.Body>
      <Modal.Footer>
        <Button
          content="Export"
          variant={buttonVariants.primary}
          onClick={handleExportClick}
        />
        <Button
          content="Cancel"
          variant={buttonVariants.secondary}
          disabled={false}
          onClick={handleCancel}
        />
      </Modal.Footer>
    </Modal>
  );
};

export default ExportContentModalXlsx;

ExportContentModalXlsx.propTypes = {
  documentsWithTerms: PropTypes.arrayOf(
    PropTypes.shape({
      docId: PropTypes.number,
      selectedTerms: PropTypes.arrayOf(PropTypes.string),
    })
  ),
  origin: PropTypes.oneOf([
    exportOrigin.documents,
    exportOrigin.simpleShred,
    exportOrigin.catShred,
  ]),
  extensions: PropTypes.shape({
    wordSelected: PropTypes.bool,
    pdfSelected: PropTypes.bool,
  }),
};
